import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-58500038"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "main-content"
}
const _hoisted_2 = { class: "main-content__content" }

import liff from "@line/liff";

/**
 * Vueの基本機能
 * - onBeforeMount: コンポーネントマウント前の処理
 * - provide: 子コンポーネントへの値の提供
 * - ref: リアクティブな参照の作成
 */
import { onBeforeMount, provide, ref } from "vue";

/**
 * Vuexストア
 * アプリケーションの状態管理に使用
 */
import { useStore } from "vuex";

/**
 * Vue Router
 * ルーティング情報の取得に使用
 */
import { useRoute } from "vue-router";

/**
 * ルーティング定数
 * ルート名やパスの定義に使用
 */
import RouterConstants from "../constants/router.constants";

/**
 * LINEアカウントの型定義
 * LINEログイン時のアカウント情報の型定義
 */
import type { ILineAccount } from "../typings";

/**
 * Vueのリアクティブユーティリティ
 * リアクティブな値の設定に使用
 */
import { set } from "@vueuse/core";

/**
 * サービス
 * - LineAccountServices: LINEアカウント関連のAPI呼び出し
 * - ClinicServices: クリニック情報関連のAPI呼び出し
 */
import { LineAccountServices, ClinicServices } from "../services";

/**
 * アプリケーション定数
 * アプリケーション全体で使用される定数
 */
import CommonConstants from "@/common/constants/app.constants";

/**
 * PrimeVueコンポーネント
 * - ConfirmDialog: 確認ダイアログ
 * - Toast: トースト通知
 */
import ConfirmDialog from "primevue/confirmdialog";
import Toast from "primevue/toast";

/**
 * Vue Routerのルート情報
 * 現在のルート情報とクエリパラメータへのアクセスに使用
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'MainContent',
  setup(__props) {

/**
 * メインコンテンツコンポーネント
 * LINEログインの管理と、ログイン後のメインコンテンツの表示を担当
 * クリニックIDの管理、LINEログイン処理、LIFF初期化などの機能を提供
 */

/**
 * LINE LIFF SDK
 * LINEログイン機能とLIFF初期化に使用
 */
const route = useRoute();

/**
 * Vuexストアインスタンス
 * アプリケーションの状態管理に使用
 */
const store = useStore();

/**
 * ログイン状態を管理するリアクティブな変数
 * true: ログイン済み
 * false: 未ログイン
 */
const hasLoggedIn = ref(false);

/**
 * 現在選択中のクリニックIDを管理するリアクティブな変数
 * 0: 未選択
 */
const clinicId = ref(0);

/**
 * クリニックIDを設定する
 *
 * @function
 * @param {number} newId - 新しいクリニックID
 */
const setClinicId = (newId: number) => set(clinicId, newId);

/**
 * ログイン状態を true に設定する
 *
 * @function
 */
const loggedIn = () => set(hasLoggedIn, true);

/**
 * ログイン成功時の処理を実行する
 *
 * @function
 * @param {ILineAccount} account - LINE アカウント情報
 */
const successLoggedIn = (account: ILineAccount) => {
  loggedIn();
  store.commit("line_account/setAccount", account);
  store.dispatch("line_account/saveClinicIdCookie");
  store.dispatch("line_account/saveLineUserIdCookie");
};

/**
 * LINEログイン処理を実行する
 * LINEアカウントサービスのcreateメソッドを呼び出し、
 * クリニックIDとLINE IDトークンを使用してログイン処理を行う
 *
 * @async
 * @param {number} clinicId - ログイン対象のクリニックID
 * @returns {Promise<void>}
 */
const lineLogin = async (clinicId: number): Promise<void> => {
  await LineAccountServices.create(
    clinicId,
    liff.getIDToken(),
    store.dispatch("line_account/getLineUserIdCookie"),
    successLoggedIn
  );
};

// クリニックIDを子コンポーネントに提供
provide("clinicId", clinicId);

/**
 * コンポーネントのマウント前に初期化処理を実行する
 * - クエリパラメータの取得と処理
 * - LIFFの初期化
 * - ログイン状態の確認と処理
 * - クリニック情報の取得
 */
onBeforeMount(async () => {
  // ルートクエリパラメータから必要な値を取得
  const { clinic_id, token, message_type, "liff.state": liffState } = route.query;

  // liffState 文字列から URLSearchParams を生成
  const urlParams = new URLSearchParams(liffState);
  // クエリまたはURLパラメータから clinic_id を取得
  const clinicIdFromUrl = clinic_id || urlParams.get("clinic_id");
  // token を同様に取得
  const routeQueryToken = token || urlParams.get("token");
  // message_type を同様に取得
  const routeQueryMessageType = message_type || urlParams.get("message_type");

  // clinicIdFromUrl が存在する場合、ストアに設定
  if (clinicIdFromUrl != undefined) {
    store.commit("line_account/setClinicId", clinicIdFromUrl);
  }
  // クリニック詳細を取得
  const { data } = await ClinicServices.fetchDetails(clinicIdFromUrl || store.getters["line_account/clinicId"]);

  // LIFF ID を初期化
  let liffId = "";
  // 履歴ページかどうかで使用する LIFF ID を分岐
  if (route.name == RouterConstants.BOOKING_HISTORY_ROUTER_NAME) {
    liffId = data.value.history_liff_id;
  } else {
    liffId = data.value.booking_liff_id;
  }
  // LIFF を初期化
  await liff.init({ liffId: liffId });

  // クリニック名をタイトルに設定
  if (data.value && data.value.name) {
    document.title = data.value.name;
    // 念のため少し遅延させて再設定
    setTimeout(() => {
      document.title = data.value.name;
    }, 100);
  }

  // ログイン状態かをチェック
  if (!liff.isLoggedIn()) {
    // リダイレクト先の URL を構築
    let redirectUri = `${CommonConstants.BASE_URL}${route.fullPath}`;
    // トークンとメッセージタイプがある場合、URL に追加
    if (routeQueryToken && routeQueryMessageType) {
      redirectUri += `&token=${routeQueryToken}&message_type=${routeQueryMessageType}`;
    }
    // LIFF ログインを実行
    return liff.login({ redirectUri: redirectUri });
  }

  // クリニックIDの設定とメッセージ情報の保存
  const resolvedClinicId = clinicIdFromUrl || store.getters["line_account/clinicId"];
  setClinicId(resolvedClinicId as unknown as number);
  // メッセージ情報をストアに設定
  store.commit("booking/setMessage", { token: routeQueryToken, type: routeQueryMessageType });

  // LINEログイン処理の実行
  await lineLogin(resolvedClinicId);

  // ログイン後にもクリニック名をタイトルに設定
  if (data.value && data.value.name) {
    document.title = data.value.name;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (hasLoggedIn.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_router_view)
          ]),
          _createVNode(_unref(ConfirmDialog))
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(Toast), {
      position: "top-center",
      errorIcon: ""
    })
  ], 64))
}
}

})